import React, { useState, useCallback, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Briefcase, Shield, Award, CheckCircle, Star, ChevronLeft, ChevronRight, Scale, Users } from 'lucide-react';
import useEmblaCarousel from 'embla-carousel-react';
import Autoplay from 'embla-carousel-autoplay';
import { 
  heroData, 
  services,
  serviceCards, 
  howItWorks, 
  testimonials, 
  faqs 
} from '../data/mock';
import ContactFormModal from '../components/ContactFormModal';
import { submitContactForm, fetchGeolocation } from '../services/api';
import { toast } from 'sonner';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '../components/ui/accordion';

const countryCodes = [
  { code: '+1', country: 'US/CA', countryCode: 'US' },
  { code: '+44', country: 'UK', countryCode: 'GB' },
  { code: '+91', country: 'IN', countryCode: 'IN' },
  { code: '+61', country: 'AU', countryCode: 'AU' },
  { code: '+81', country: 'JP', countryCode: 'JP' },
  { code: '+31', country: 'NL', countryCode: 'NL' },
  { code: '+92', country: 'PK', countryCode: 'PK' },
  { code: '+49', country: 'DE', countryCode: 'DE' },
  { code: '+33', country: 'FR', countryCode: 'FR' },
  { code: '+971', country: 'AE', countryCode: 'AE' }
];

const iconMap = {
  briefcase: Briefcase,
  shield: Shield,
  award: Award
};

const HomePage = () => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [ctaFormData, setCtaFormData] = useState({
    name: '',
    email: '',
    countryCode: '+1',
    country: 'US',
    phone: '',
    message: ''
  });
  const [isCtaSubmitting, setIsCtaSubmitting] = useState(false);
  const [ctaErrors, setCtaErrors] = useState({});
  const [ctaGeoData, setCtaGeoData] = useState(null);

  // Fetch geolocation on component mount
  useEffect(() => {
    fetchGeolocation()
      .then(data => {
        if (data && data.isSuccess && data.data) {
          setCtaGeoData(data);
          const countryCode = data.data.countryCode;
          const matchedCountry = countryCodes.find(c => c.countryCode === countryCode);
          if (matchedCountry) {
            setCtaFormData(prev => ({
              ...prev,
              countryCode: matchedCountry.code,
              country: matchedCountry.countryCode
            }));
          }
        }
      })
      .catch(error => {
        console.error('Failed to fetch geolocation:', error);
      });
  }, []);

  const validateCtaForm = () => {
    const newErrors = {};

    if (!ctaFormData.name.trim()) {
      newErrors.name = 'Name is required';
    }

    if (!ctaFormData.email.trim()) {
      newErrors.email = 'Email is required';
    } else if (!/\S+@\S+\.\S+/.test(ctaFormData.email)) {
      newErrors.email = 'Email is invalid';
    }

    if (!ctaFormData.phone.trim()) {
      newErrors.phone = 'Phone number is required';
    } else if (!/^\d{7,15}$/.test(ctaFormData.phone)) {
      newErrors.phone = 'Phone number must be between 7 and 15 digits';
    }

    if (!ctaFormData.message.trim()) {
      newErrors.message = 'Message is required';
    }

    setCtaErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleCtaPhoneChange = (e) => {
    const value = e.target.value.replace(/\D/g, '');
    setCtaFormData({ ...ctaFormData, phone: value });
  };

  const handleCtaCountryCodeChange = (e) => {
    const selectedCode = e.target.value;
    const matchedCountry = countryCodes.find(c => c.code === selectedCode);
    setCtaFormData({ 
      ...ctaFormData, 
      countryCode: selectedCode,
      country: matchedCountry?.countryCode || 'US'
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateCtaForm()) {
      return;
    }

    setIsCtaSubmitting(true);

    try {
      const result = await submitContactForm(ctaFormData, ctaGeoData);
      
      toast.success(result.data?.message || 'Form submitted successfully!');
      
      // Form will redirect automatically on success
    } catch (error) {
      toast.error(error.message || "Failed to submit form. Please try again.");
      setIsCtaSubmitting(false);
    }
  };


  useEffect(() => {
    document.title = "Home - IP Partners Inc | Professional Legal Services";
  }, []);
  
  // Services carousel
  const [servicesEmblaRef, servicesEmblaApi] = useEmblaCarousel(
    { loop: true, align: 'start' },
    [Autoplay({ delay: 3000, stopOnInteraction: false })]
  );

  // Testimonials carousel - show 3 at a time
  const [testimonialsEmblaRef, testimonialsEmblaApi] = useEmblaCarousel(
    { loop: true, align: 'start', slidesToScroll: 1 },
    [Autoplay({ delay: 4000, stopOnInteraction: false })]
  );

  const scrollServicesNext = useCallback(() => {
    if (servicesEmblaApi) servicesEmblaApi.scrollNext();
  }, [servicesEmblaApi]);

  const scrollServicesPrev = useCallback(() => {
    if (servicesEmblaApi) servicesEmblaApi.scrollPrev();
  }, [servicesEmblaApi]);

  const scrollTestimonialsNext = useCallback(() => {
    if (testimonialsEmblaApi) testimonialsEmblaApi.scrollNext();
  }, [testimonialsEmblaApi]);

  const scrollTestimonialsPrev = useCallback(() => {
    if (testimonialsEmblaApi) testimonialsEmblaApi.scrollPrev();
  }, [testimonialsEmblaApi]);

  return (
    <div className="min-h-screen bg-brown-50">
      {/* Hero Section */}
      <section className="relative bg-gradient-to-br from-brown-100 to-brown-50 pt-32 pb-20 lg:pt-40 lg:pb-28">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-brown-900 leading-tight mb-6">
                {heroData.title}
              </h1>
              <p className="text-lg sm:text-xl text-brown-700 mb-8 leading-relaxed">
                {heroData.subtitle}
              </p>
              <button
                onClick={() => setIsModalOpen(true)}
                className="bg-brown-700 text-white px-8 py-4 rounded-lg font-semibold text-lg hover:bg-brown-800 transition-all duration-200 shadow-lg hover:shadow-xl inline-flex items-center space-x-2"
              >
                <span>{heroData.ctaText}</span>
                <ArrowRight className="w-5 h-5" />
              </button>
            </div>
            <div className="relative">
              <img
                src={heroData.heroImage}
                alt="Legal consultation"
                className="rounded-xl shadow-2xl w-full h-[400px] object-cover ring-4 ring-brown-200"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Informative Section 1 */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <img
                src="https://images.pexels.com/photos/4427430/pexels-photo-4427430.jpeg"
                alt="Legal team collaboration"
                className="rounded-xl shadow-xl w-full h-[450px] object-cover ring-2 ring-brown-300"
              />
            </div>
            <div>
              <h2 className="text-3xl sm:text-4xl font-bold text-brown-900 mb-6">
                Expert Legal Counsel You Can Trust
              </h2>
              <p className="text-lg text-brown-700 mb-6 leading-relaxed">
                With decades of combined experience, our attorneys provide strategic legal guidance 
                tailored to your unique business needs. We understand the complexities of corporate 
                law and deliver solutions that protect your interests and drive success.
              </p>
              <ul className="space-y-4 mb-8">
                <li className="flex items-start space-x-3">
                  <CheckCircle className="w-6 h-6 text-brown-600 flex-shrink-0 mt-1" />
                  <span className="text-brown-700">Comprehensive legal expertise across multiple practice areas</span>
                </li>
                <li className="flex items-start space-x-3">
                  <CheckCircle className="w-6 h-6 text-brown-600 flex-shrink-0 mt-1" />
                  <span className="text-brown-700">Personalized attention and responsive communication</span>
                </li>
                <li className="flex items-start space-x-3">
                  <CheckCircle className="w-6 h-6 text-brown-600 flex-shrink-0 mt-1" />
                  <span className="text-brown-700">Proven track record of successful outcomes</span>
                </li>
              </ul>
              <button
                onClick={() => setIsModalOpen(true)}
                className="bg-brown-700 text-white px-6 py-3 rounded-lg font-semibold hover:bg-brown-800 transition-colors duration-200 inline-flex items-center space-x-2"
              >
                <span>Schedule Consultation</span>
                <ArrowRight className="w-5 h-5" />
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* Informative Section 2 */}
      <section className="py-20 bg-brown-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div className="order-2 lg:order-1">
              <h2 className="text-3xl sm:text-4xl font-bold text-brown-900 mb-6">
                Committed to Your Success
              </h2>
              <p className="text-lg text-brown-700 mb-6 leading-relaxed">
                At IP Partners Inc, we don't just provide legal services – we build lasting partnerships. 
                Our commitment to excellence means we go above and beyond to understand your goals and 
                deliver strategies that align with your vision.
              </p>
              <div className="grid grid-cols-2 gap-6 mb-8">
                <div className="text-center p-6 bg-white rounded-lg shadow-md">
                  <div className="text-3xl font-bold text-brown-700 mb-2">500+</div>
                  <div className="text-sm text-brown-600">Businesses Served</div>
                </div>
                <div className="text-center p-6 bg-white rounded-lg shadow-md">
                  <div className="text-3xl font-bold text-brown-700 mb-2">98%</div>
                  <div className="text-sm text-brown-600">Client Satisfaction</div>
                </div>
                <div className="text-center p-6 bg-white rounded-lg shadow-md">
                  <div className="text-3xl font-bold text-brown-700 mb-2">25+</div>
                  <div className="text-sm text-brown-600">Years Experience</div>
                </div>
                <div className="text-center p-6 bg-white rounded-lg shadow-md">
                  <div className="text-3xl font-bold text-brown-700 mb-2">50</div>
                  <div className="text-sm text-brown-600">States Covered</div>
                </div>
              </div>
            </div>
            <div className="order-1 lg:order-2">
              <img
                src="https://images.unsplash.com/photo-1589578527966-fdac0f44566c"
                alt="Lady Justice"
                className="rounded-xl shadow-xl w-full h-[450px] object-cover ring-2 ring-brown-300"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Services Carousel Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl sm:text-4xl font-bold text-brown-900 mb-4">
              Comprehensive Legal Services
            </h2>
            <p className="text-lg text-brown-700 max-w-2xl mx-auto">
              Expert legal guidance across all practice areas to meet your business needs
            </p>
          </div>

          <div className="relative">
            <div className="overflow-hidden" ref={servicesEmblaRef}>
              <div className="flex">
                {services.map((service, index) => (
                  <div key={service.slug} className="flex-[0_0_100%] min-w-0 sm:flex-[0_0_50%] lg:flex-[0_0_33.333%] px-4">
                    <Link to={`/services/${service.slug}`}>
                      <div className="bg-brown-50 rounded-xl shadow-md hover:shadow-xl transition-all duration-300 overflow-hidden group h-full border border-brown-200">
                        <div className="p-8">
                          <div className="flex items-center space-x-3 mb-4">
                            <div className="bg-brown-200 p-3 rounded-lg">
                              <Scale className="w-6 h-6 text-brown-700" />
                            </div>
                            <h3 className="text-xl font-bold text-brown-900">{service.name}</h3>
                          </div>
                          <p className="text-brown-700 text-center group-hover:text-brown-900 transition-colors">
                            Professional legal expertise
                          </p>
                        </div>
                      </div>
                    </Link>
                  </div>
                ))}
              </div>
            </div>
            
            {/* Navigation buttons */}
            <button
              onClick={scrollServicesPrev}
              className="absolute left-0 top-1/2 -translate-y-1/2 -translate-x-4 bg-brown-700 text-white p-3 rounded-full shadow-lg hover:bg-brown-800 transition-colors z-10"
            >
              <ChevronLeft className="w-6 h-6" />
            </button>
            <button
              onClick={scrollServicesNext}
              className="absolute right-0 top-1/2 -translate-y-1/2 translate-x-4 bg-brown-700 text-white p-3 rounded-full shadow-lg hover:bg-brown-800 transition-colors z-10"
            >
              <ChevronRight className="w-6 h-6" />
            </button>
          </div>
        </div>
      </section>

      {/* How It Works Section */}
      <section className="py-20 bg-brown-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl sm:text-4xl font-bold text-brown-900 mb-4">
              How It Works
            </h2>
            <p className="text-lg text-brown-700 max-w-2xl mx-auto">
              Get the legal help you need in three simple steps
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {howItWorks.map((step) => (
              <div key={step.step} className="text-center">
                <div className="bg-brown-200 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-6">
                  <span className="text-2xl font-bold text-brown-800">{step.step}</span>
                </div>
                <h3 className="text-xl font-bold text-brown-900 mb-3">{step.title}</h3>
                <p className="text-brown-700">{step.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Testimonials Carousel Section */}
      <section className="py-20 bg-brown-900">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl sm:text-4xl font-bold text-white mb-4">
              What Our Clients Say
            </h2>
            <p className="text-lg text-brown-200">
              Real experiences from satisfied clients across the nation
            </p>
          </div>

          <div className="relative">
            <div className="overflow-hidden" ref={testimonialsEmblaRef}>
              <div className="flex">
                {testimonials.map((testimonial) => (
                  <div key={testimonial.id} className="flex-[0_0_100%] md:flex-[0_0_50%] lg:flex-[0_0_33.333%] min-w-0 px-4">
                    <div className="bg-brown-800 rounded-xl p-6 shadow-xl h-full flex flex-col">
                      <div className="flex mb-4 justify-center">
                        {[...Array(testimonial.rating)].map((_, i) => (
                          <Star key={i} className="w-5 h-5 text-yellow-400 fill-current" />
                        ))}
                      </div>
                      <p className="text-brown-100 text-base mb-6 italic text-center leading-relaxed flex-grow">
                        "{testimonial.content}"
                      </p>
                      <div className="text-center">
                        <p className="font-semibold text-white text-lg">{testimonial.name}</p>
                        <p className="text-sm text-brown-300">{testimonial.role}</p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Navigation buttons */}
            <button
              onClick={scrollTestimonialsPrev}
              className="absolute left-0 top-1/2 -translate-y-1/2 -translate-x-4 bg-brown-700 text-white p-3 rounded-full shadow-lg hover:bg-brown-600 transition-colors"
            >
              <ChevronLeft className="w-6 h-6" />
            </button>
            <button
              onClick={scrollTestimonialsNext}
              className="absolute right-0 top-1/2 -translate-y-1/2 translate-x-4 bg-brown-700 text-white p-3 rounded-full shadow-lg hover:bg-brown-600 transition-colors"
            >
              <ChevronRight className="w-6 h-6" />
            </button>
          </div>
        </div>
      </section>

      {/* FAQ Section with Image */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl sm:text-4xl font-bold text-brown-900 mb-4">
              Frequently Asked Questions
            </h2>
            <p className="text-lg text-brown-700">
              Find answers to common questions about our services
            </p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
            {/* Image on Left */}
            <div className="flex items-start">
              <div className="sticky top-24 w-full">
                <img
                  src="https://images.pexels.com/photos/8112172/pexels-photo-8112172.jpeg"
                  alt="Professional consultation"
                  className="rounded-xl shadow-xl w-full h-[550px] object-cover ring-2 ring-brown-300"
                />
              </div>
            </div>

            {/* FAQ Content on Right */}
            <div className="flex items-start">
              <Accordion type="single" collapsible className="space-y-4 w-full">
                {faqs.map((faq, index) => (
                  <AccordionItem
                    key={index}
                    value={`item-${index}`}
                    className="bg-brown-50 rounded-lg shadow-sm border border-brown-200"
                  >
                    <AccordionTrigger className="px-6 py-4 text-left hover:no-underline hover:bg-brown-100 transition-colors rounded-lg">
                      <span className="font-semibold text-brown-900">{faq.question}</span>
                    </AccordionTrigger>
                    <AccordionContent className="px-6 pb-4 text-brown-700">
                      {faq.answer}
                    </AccordionContent>
                  </AccordionItem>
                ))}
              </Accordion>
            </div>
          </div>
        </div>
      </section>

      {/* Contact Form Section */}
      <section className="py-20 bg-gradient-to-br from-brown-900 to-brown-800">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            {/* Form Column - Left */}
            <div className="order-2 lg:order-1">
              <div className="bg-white rounded-2xl shadow-2xl p-8">
                <h3 className="text-2xl font-bold text-brown-900 mb-6">Send Us a Message</h3>
                <form onSubmit={handleSubmit} className="space-y-4">
                  {/* Name */}
                  <div>
                    <label htmlFor="cta-name" className="block text-sm font-medium text-brown-900 mb-1">
                      Full Name *
                    </label>
                    <input
                      type="text"
                      id="cta-name"
                      value={ctaFormData.name}
                      onChange={(e) => setCtaFormData({ ...ctaFormData, name: e.target.value })}
                      className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-brown-600 focus:border-brown-600 outline-none transition-colors ${
                        ctaErrors.name ? 'border-red-500' : 'border-gray-300'
                      }`}
                      placeholder="John Doe"
                    />
                    {ctaErrors.name && <p className="mt-1 text-sm text-red-600">{ctaErrors.name}</p>}
                  </div>

                  {/* Email */}
                  <div>
                    <label htmlFor="cta-email" className="block text-sm font-medium text-brown-900 mb-1">
                      Email Address *
                    </label>
                    <input
                      type="email"
                      id="cta-email"
                      value={ctaFormData.email}
                      onChange={(e) => setCtaFormData({ ...ctaFormData, email: e.target.value })}
                      className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-brown-600 focus:border-brown-600 outline-none transition-colors ${
                        ctaErrors.email ? 'border-red-500' : 'border-gray-300'
                      }`}
                      placeholder="john@example.com"
                    />
                    {ctaErrors.email && <p className="mt-1 text-sm text-red-600">{ctaErrors.email}</p>}
                  </div>

                  {/* Phone */}
                  <div>
                    <label htmlFor="cta-phone" className="block text-sm font-medium text-brown-900 mb-1">
                      Contact Number *
                    </label>
                    <div className="flex space-x-2">
                      <select
                        value={ctaFormData.countryCode}
                        onChange={handleCtaCountryCodeChange}
                        className="px-3 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brown-600 focus:border-brown-600 outline-none bg-white"
                      >
                        {countryCodes.map((item) => (
                          <option key={item.code} value={item.code}>
                            {item.code} ({item.country})
                          </option>
                        ))}
                      </select>
                      <input
                        type="tel"
                        id="cta-phone"
                        value={ctaFormData.phone}
                        onChange={handleCtaPhoneChange}
                        className={`flex-1 px-4 py-3 border rounded-lg focus:ring-2 focus:ring-brown-600 focus:border-brown-600 outline-none transition-colors ${
                          ctaErrors.phone ? 'border-red-500' : 'border-gray-300'
                        }`}
                        placeholder="1234567890"
                      />
                    </div>
                    {ctaErrors.phone && <p className="mt-1 text-sm text-red-600">{ctaErrors.phone}</p>}
                  </div>

                  {/* Message */}
                  <div>
                    <label htmlFor="cta-message" className="block text-sm font-medium text-brown-900 mb-1">
                      Describe Your Legal Needs *
                    </label>
                    <textarea
                      id="cta-message"
                      value={ctaFormData.message}
                      onChange={(e) => setCtaFormData({ ...ctaFormData, message: e.target.value })}
                      rows="4"
                      className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-brown-600 focus:border-brown-600 outline-none transition-colors resize-none ${
                        ctaErrors.message ? 'border-red-500' : 'border-gray-300'
                      }`}
                      placeholder="Tell us about your legal situation..."
                    />
                    {ctaErrors.message && <p className="mt-1 text-sm text-red-600">{ctaErrors.message}</p>}
                  </div>

                  {/* Submit Button */}
                  <button
                    type="submit"
                    disabled={isCtaSubmitting}
                    className="w-full bg-brown-700 text-white px-6 py-3 rounded-lg font-semibold text-lg hover:bg-brown-800 transition-colors duration-200 disabled:opacity-50 disabled:cursor-not-allowed shadow-lg"
                  >
                    {isCtaSubmitting ? 'Submitting...' : 'Submit Request'}
                  </button>

                  <p className="text-xs text-gray-500 text-center mt-3">
                    By submitting this form, you agree to our Terms of Service and Privacy Policy.
                  </p>
                </form>
              </div>
            </div>

            {/* Content Column - Right */}
            <div className="order-1 lg:order-2 text-white">
              <h2 className="text-4xl sm:text-5xl font-bold mb-6 leading-tight">
                Ready to Get Started?
              </h2>
              <p className="text-xl text-brown-100 mb-8 leading-relaxed">
                Connect with an experienced attorney today and get the legal help you need. We're here to guide you through your legal matters with expertise and care.
              </p>
              
              <div className="space-y-6">
                <div className="flex items-start space-x-4">
                  <div className="flex-shrink-0">
                    <div className="w-12 h-12 bg-brown-600 rounded-lg flex items-center justify-center">
                      <CheckCircle className="w-6 h-6 text-white" />
                    </div>
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold mb-1">Fast Response</h3>
                    <p className="text-brown-200">Get a response within 24 hours from our legal team</p>
                  </div>
                </div>

                <div className="flex items-start space-x-4">
                  <div className="flex-shrink-0">
                    <div className="w-12 h-12 bg-brown-600 rounded-lg flex items-center justify-center">
                      <Shield className="w-6 h-6 text-white" />
                    </div>
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold mb-1">Confidential & Secure</h3>
                    <p className="text-brown-200">Your information is protected with industry-leading security</p>
                  </div>
                </div>

                <div className="flex items-start space-x-4">
                  <div className="flex-shrink-0">
                    <div className="w-12 h-12 bg-brown-600 rounded-lg flex items-center justify-center">
                      <Users className="w-6 h-6 text-white" />
                    </div>
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold mb-1">Expert Attorneys</h3>
                    <p className="text-brown-200">Work with experienced legal professionals in your area</p>
                  </div>
                </div>
              </div>

              <div className="mt-10 pt-8 border-t border-brown-600">
                <p className="text-brown-200 mb-2">Or call us directly:</p>
                <a href="tel:+18332017574" className="text-2xl font-bold text-white hover:text-brown-300 transition-colors">
                  +1 (833) 201-7574
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>

      <ContactFormModal isOpen={isModalOpen} onClose={() => setIsModalOpen(false)} />
    </div>
  );
};

export default HomePage;
